import { useMemo, type ReactNode } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { seedColorForSubArea, subAreaColor } from '@domain/entities/Organization';
import { getServices } from '@infrastructure/services';
import { Avatar, Card, Chip, EmptyState, Spinner, cssVars, cx } from '../../design-system';
import { formatDateRange, MONTHS_LONG, statusChipColors } from '../../format';
import { usePersonLink } from '../../hooks/usePersonLink';
import { useScopedAreaId } from '../../providers/AreaFocusProvider';
import { peopleFilterFor } from './searchScope';
import { useAccess } from '../../hooks/useAccess';
import { useApprovalScope } from '../../hooks/useApprovalScope';
import { useSession } from '../../providers/SessionProvider';
import { matchesAny, normalise, showsGroup } from './searchMatching';
import styles from './SearchScreen.module.css';

/**
 * Búsqueda global — transcribed from
 * `design/reference/screens/busqueda-global.html`.
 *
 * A screen rather than an overlay, which is what the prototype does and what a
 * search over records wants: the query lives in the URL, so a result is
 * shareable and the back button behaves.
 *
 * Everything it can find is already bounded by what the session may read. A
 * person with only `calendario_propio` searches their own sub area; someone
 * with neither sees no people at all. That is the same rule the calendar
 * applies, restated here rather than widened — a search box is the classic
 * place a permission model leaks.
 */

const RESULT_LIMIT = 20;

interface ResultRow {
  readonly id: string;
  /** Shown instead of an avatar — a holiday's day number. */
  readonly initials: string;
  /**
   * Whose initials the avatar draws. Distinct from `title`, because a request
   * is titled by its folio and initialling "VC-0214" gives "V".
   */
  readonly avatarName: string;
  readonly color: string;
  readonly title: string;
  readonly detail: string;
  readonly tagLabel: string;
  readonly tagBackground: string;
  readonly tagForeground: string;
  readonly onOpen: () => void;
}

interface ResultGroup {
  readonly key: string;
  readonly label: string;
  readonly color: string;
  readonly rows: readonly ResultRow[];
}

export const SearchScreen = (): ReactNode => {
  const session = useSession();
  const { holds, featureOn } = useAccess();
  const approval = useApprovalScope();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();

  const query = params.get('q') ?? '';
  const scope = params.get('scope') ?? 'Todo';
  const trimmed = query.trim();
  const needle = normalise(trimmed);
  const isSuper = session.role === 'super';
  /*
   * El área en foco, como el calendario, los reportes, el padrón y la bitácora.
   *
   * La búsqueda era la única pantalla de gestión que seguía mirando
   * `session.areaId`, así que un `global` cambiaba de área con el selector y el
   * buscador seguía contestando sobre la suya — devolviendo resultados, que es
   * la forma callada de equivocarse.
   */
  const areaId = useScopedAreaId(session.areaId);

  /*
   * `ver_detalle` también abre el padrón de la búsqueda.
   *
   * Antes esto miraba sólo los dos permisos de calendario, y con eso bastaba
   * mientras todo el que abría una ficha llegara desde un avatar del calendario o
   * del tablero. El rol `finanzas` rompe esa suposición: tiene `ver_detalle` para
   * fijar sueldos y **ninguna pantalla que liste gente**, así que entraba, veía
   * «Inicio» y no tenía ninguna forma de llegar a una persona.
   *
   * Quien puede abrir una ficha tiene que poder encontrar a su dueño. El alcance
   * no se amplía: la consulta sigue acotada al área de quien mira, y quien no
   * tenga ninguno de los tres sigue sin ver a nadie.
   */
  const canSeeArea = holds('calendario_area') || holds('ver_detalle');
  const openPerson = usePersonLink();
  const canSeeOwnSubArea = holds('calendario_propio');
  const active = (key: string): boolean => scope === 'Todo' || scope === key;

  const tabs = isSuper
    ? ['Todo', 'Feriados', 'Sub áreas']
    : ['Todo', 'Personas', 'Solicitudes', 'Feriados', 'Sub áreas'];

  const enabled = needle.length > 0;

  const people = useQuery({
    queryKey: ['search', 'people', areaId, session.subAreaId, canSeeArea],
    enabled: enabled && active('Personas') && (canSeeArea || canSeeOwnSubArea) && !isSuper,
    queryFn: async () => {
      // Narrowed to the sub area for someone who may only see their own — the
      // wider read is not made and then filtered, it is never made. Y sobre el
      // área en foco, no sobre la propia: ver `peopleFilterFor`.
      const result = await getServices().people.list(
        peopleFilterFor({
          areaId,
          ownAreaId: session.areaId,
          ownSubAreaId: session.subAreaId,
          canSeeArea,
        }),
        { limit: 200 },
      );
      return result.ok ? result.value.items : [];
    },
  });

  const subAreas = useQuery({
    queryKey: ['search', 'subAreas', areaId],
    enabled: enabled && active('Sub áreas'),
    queryFn: async () => {
      const result = await getServices().organization.listSubAreas(areaId);
      return result.ok ? result.value : [];
    },
  });

  const requests = useQuery({
    queryKey: ['search', 'requests', session.personId, approval.scope, holds('aprobar')],
    enabled: enabled && active('Solicitudes') && !isSuper && !approval.loading,
    queryFn: async () => {
      const services = getServices();
      const own = holds('solicitar')
        ? await services.requests.list({ personId: session.personId }, { limit: 50 })
        : null;
      const queue = holds('aprobar')
        ? await services.requests.list(approval.scope, { limit: 100 })
        : null;

      const rows = [
        ...(own?.ok === true ? own.value.items : []),
        ...(queue?.ok === true ? approval.mine(queue.value.items) : []),
      ];

      // A leader's own request can arrive from both reads.
      return [...new Map(rows.map((r) => [r.id, r])).values()];
    },
  });

  const holidays = useQuery({
    queryKey: ['search', 'holidays'],
    enabled: enabled && active('Feriados') && featureOn('feriados'),
    queryFn: async () => {
      const year = getServices().clock.today().year;
      const result = await getServices().holidays.listForYear(year);
      return result.ok ? result.value : [];
    },
  });

  const groups = useMemo<ResultGroup[]>(() => {
    if (needle.length === 0) return [];
    /*
     * La pestaña se aplica **aquí**, no sólo en el `enabled` de cada consulta.
     *
     * Acotarla nada más al consultar apaga la red y deja la caché intacta: quien
     * buscaba en «Todo» y luego elegía «Sub áreas» seguía viendo personas y
     * solicitudes de la búsqueda anterior, y la pestaña parecía no hacer nada.
     * Un grupo se dibuja si le toca a la pestaña y si tiene filas, en ese orden.
     */
    const built: ResultGroup[] = [];

    const subAreaName = (id: string | null): string =>
      (subAreas.data ?? []).find((s) => s.id === id)?.name ?? '';

    const personRows: ResultRow[] = (people.data ?? [])
      /* Por su nombre, su correo o su sub área: la fila enseña las tres, así que
         las tres son maneras legítimas de haberla encontrado. */
      .filter((p) => matchesAny(needle, p.displayName, p.email, subAreaName(p.subAreaId)))
      .slice(0, RESULT_LIMIT)
      .map((p) => ({
        id: p.id,
        initials: '',
        avatarName: p.displayName,
        color: seedColorForSubArea(subAreaName(p.subAreaId) || p.displayName),
        title: p.displayName,
        detail: [subAreaName(p.subAreaId) || 'Sin sub área', p.email].filter(Boolean).join(' · '),
        /*
         * Un resultado de persona lleva a la persona, si quien busca puede
         * abrirla. Antes llevaba siempre al calendario del área, y con eso bastaba
         * mientras todo el que buscaba tuviera calendario.
         *
         * El rol `finanzas` rompió esa suposición y lo hizo visible: no tiene
         * permiso de calendario, así que buscar a alguien, pulsarlo y **acabar en
         * Inicio sin explicación** era su único camino. La guarda de ruta rebota
         * en silencio —no es un fallo de permiso con mensaje, es un destino que
         * esa sesión no puede abrir—, que es la peor forma de decir que no.
         */
        tagLabel: openPerson(p.id) !== null ? 'Ver su ficha' : 'Ver en calendario',
        tagBackground: 'var(--color-brand-bg-alt)',
        tagForeground: 'var(--color-brand-ink)',
        onOpen:
          openPerson(p.id) ??
          (() => {
            void navigate('/calendario');
          }),
      }));
    if (showsGroup(scope, 'Personas') && personRows.length > 0) {
      built.push({
        key: 'personas',
        label: 'Personas',
        color: 'var(--color-brand)',
        rows: personRows,
      });
    }

    const requestRows: ResultRow[] = (requests.data ?? [])
      /* Folio, de quién es, en qué estado está y de qué sub área: todo eso lo
         dice la propia tarjeta del resultado. */
      .filter((r) => matchesAny(needle, r.folio, r.personName, r.status, r.subAreaName))
      .slice(0, RESULT_LIMIT)
      .map((r) => {
        const colors = statusChipColors(r.status);
        return {
          id: r.id,
          initials: '',
          avatarName: r.personName,
          color: seedColorForSubArea(r.subAreaName),
          title: `${r.folio} · ${r.personName}`,
          detail: `${formatDateRange(r.range)} · ${String(r.businessDays)} ${
            r.businessDays === 1 ? 'día hábil' : 'días hábiles'
          }`,
          tagLabel: r.status,
          tagBackground: colors.background,
          tagForeground: colors.foreground,
          onOpen: () => {
            void navigate(
              r.personId === session.personId && holds('solicitar')
                ? '/ausencias'
                : '/aprobaciones',
            );
          },
        };
      });
    if (showsGroup(scope, 'Solicitudes') && requestRows.length > 0) {
      built.push({
        key: 'solicitudes',
        label: 'Solicitudes',
        color: 'var(--color-success)',
        rows: requestRows,
      });
    }

    const holidayRows: ResultRow[] = (holidays.data ?? [])
      /* Por su nombre o por su mes, que es lo que la fila enseña. No por caer
         dentro de una solicitud que sí coincide: eso llenaría la pestaña de
         feriados que no contienen lo que se escribió. */
      .filter((h) => matchesAny(needle, h.name, MONTHS_LONG[h.date.month - 1] ?? null))
      .slice(0, RESULT_LIMIT)
      .map((h) => ({
        id: h.id,
        initials: String(h.date.day),
        avatarName: h.name,
        color: 'var(--color-violet)',
        title: h.name,
        detail: `${String(h.date.day)} ${MONTHS_LONG[h.date.month - 1] ?? ''} · ${h.type}`,
        tagLabel: h.enabled ? 'Activo' : 'Inactivo',
        tagBackground: h.enabled ? 'var(--color-success-bg)' : 'var(--color-surface-subtle-alt)',
        tagForeground: h.enabled
          ? 'var(--color-success-ink)'
          : 'var(--color-text-tertiary-alt)',
        onOpen: () => {
          void navigate(isSuper ? '/configuracion' : '/calendario');
        },
      }));
    if (showsGroup(scope, 'Feriados') && holidayRows.length > 0) {
      built.push({
        key: 'feriados',
        label: 'Feriados',
        color: 'var(--color-violet)',
        rows: holidayRows,
      });
    }

    const subAreaRows: ResultRow[] = (subAreas.data ?? [])
      /* Sólo por su nombre. Que alguien de la sub área coincida no la convierte
         en un resultado: lo que se buscó no está en ninguna parte de esta fila. */
      .filter((s) => matchesAny(needle, s.name))
      .slice(0, RESULT_LIMIT)
      .map((s) => ({
        id: s.id,
        initials: '',
        avatarName: s.name,
        color: subAreaColor(s),
        title: s.name,
        detail: `Máx. ${String(s.maxConcurrentOut)} ${
          s.maxConcurrentOut === 1 ? 'persona fuera' : 'personas fuera'
        } a la vez`,
        tagLabel: isSuper ? 'Ver sub áreas' : 'Ver calendario',
        tagBackground: 'var(--color-warning-bg)',
        tagForeground: 'var(--color-warning-ink)',
        onOpen: () => {
          void navigate(isSuper ? '/administrador' : '/calendario');
        },
      }));
    if (showsGroup(scope, 'Sub áreas') && subAreaRows.length > 0) {
      built.push({
        key: 'subareas',
        label: 'Sub áreas',
        color: 'var(--color-warning)',
        rows: subAreaRows,
      });
    }

    return built;
  }, [
    needle,
    scope,
    people.data,
    requests.data,
    holidays.data,
    subAreas.data,
    navigate,
    holds,
    openPerson,
    session.personId,
    isSuper,
  ]);

  const total = groups.reduce((n, g) => n + g.rows.length, 0);

  const summary =
    trimmed.length === 0
      ? isSuper
        ? 'Busca feriados y sub áreas de la configuración global.'
        : 'Escribe para buscar en personas, solicitudes, feriados y sub áreas que tu perfil puede ver.'
      : `${String(total)} ${total === 1 ? 'resultado' : 'resultados'} para «${trimmed}»${
          // Stated rather than silent: a narrower result set that looks like the
          // whole company is worse than one that says what it covers.
          canSeeArea || !canSeeOwnSubArea ? '' : ' · limitado a tu sub área'
        }`;

  const suggestions = isSuper
    ? ['Navidad', 'Backend', 'QA']
    : ['Bruno', 'VC-0207', 'Navidad', 'Desarrollo iOS'];

  const loading =
    people.isFetching || requests.isFetching || holidays.isFetching || subAreas.isFetching;

  return (
    <div className="flow">
      <header className={styles.header}>
        <div>
          <h1 className={styles.title}>Búsqueda global</h1>
          <p className={styles.summary}>{summary}</p>
        </div>
        <button
          type="button"
          className={styles.exit}
          onClick={() => {
            void navigate(-1);
          }}
        >
          Salir de la búsqueda <span className={styles.esc}>Esc</span>
        </button>
      </header>

      <div className={cx(styles.tabs, 'scroll-x')} role="group" aria-label="Acotar la búsqueda">
        {tabs.map((label) => (
          <button
            key={label}
            type="button"
            aria-pressed={scope === label}
            className={cx(styles.tab, scope === label && styles.tabActive)}
            onClick={() => {
              setParams(
                (current) => {
                  const next = new URLSearchParams(current);
                  next.set('scope', label);
                  return next;
                },
                { replace: true },
              );
            }}
          >
            {label}
          </button>
        ))}
      </div>

      {loading && total === 0 ? (
        <Card>
          <Spinner label="Buscando" />
        </Card>
      ) : total === 0 ? (
        <Card>
          <EmptyState
            glyph="⌕"
            title={trimmed.length === 0 ? 'Empieza a escribir' : `Sin resultados para «${trimmed}»`}
            note="Prueba con un nombre, un código como VC-0207, una sub área o un feriado."
          />
          <div className={styles.suggestions}>
            {suggestions.map((label) => (
              <button
                key={label}
                type="button"
                className={styles.suggestion}
                onClick={() => {
                  setParams({ q: label, scope: 'Todo' }, { replace: true });
                }}
              >
                {label}
              </button>
            ))}
          </div>
        </Card>
      ) : (
        <div className={styles.groups}>
          {groups.map((group) => (
            <section key={group.key} className={styles.group}>
              <div className={styles.groupHead}>
                <span
                  className={styles.groupDot}
                  style={cssVars({ '--group-color': group.color })}
                  aria-hidden="true"
                />
                <h2 className={styles.groupTitle}>{group.label}</h2>
                <Chip background="var(--color-surface-subtle-alt)" foreground={group.color}>
                  {group.rows.length} {group.rows.length === 1 ? 'resultado' : 'resultados'}
                </Chip>
              </div>

              <div className={styles.rows}>
                {group.rows.map((row) => (
                  <button key={row.id} type="button" className={styles.row} onClick={row.onOpen}>
                    {row.initials.length > 0 ? (
                      <span
                        className={styles.dateTile}
                        style={cssVars({ '--tile-color': row.color })}
                        aria-hidden="true"
                      >
                        {row.initials}
                      </span>
                    ) : (
                      <Avatar name={row.avatarName} color={row.color} size={36} />
                    )}
                    <span className={styles.rowText}>
                      <span className={styles.rowTitle}>{row.title}</span>
                      <span className={styles.rowDetail}>{row.detail}</span>
                    </span>
                    <span className={styles.rowTail}>
                      <Chip background={row.tagBackground} foreground={row.tagForeground}>
                        {row.tagLabel}
                      </Chip>
                      <span className={styles.arrow} aria-hidden="true">
                        →
                      </span>
                    </span>
                  </button>
                ))}
              </div>
            </section>
          ))}
        </div>
      )}
    </div>
  );
};
